"use client"

import * as SelectPrimitive from "@radix-ui/react-select"
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react"
import * as React from "react"
import { cn } from "../../utils"

function Select({ ...props }: React.ComponentProps<typeof SelectPrimitive.Root>) {
	return <SelectPrimitive.Root data-slot="select" {...props} />
}

function SelectGroup({ ...props }: React.ComponentProps<typeof SelectPrimitive.Group>) {
	return <SelectPrimitive.Group data-slot="select-group" className="flex flex-col gap-1" {...props} />
}

function SelectValue({ ...props }: React.ComponentProps<typeof SelectPrimitive.Value>) {
	return <SelectPrimitive.Value data-slot="select-value" {...props} />
}

function SelectTrigger({
	className,
	size = "small",
	children,
	...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
	size?: "small" | "medium"
}) {
	return (
		<SelectPrimitive.Trigger
			data-slot="select-trigger"
			data-size={size}
			className={cn(
				"flex w-full items-center justify-between gap-2 rounded-lg bg-g100 border border-transparent pl-3 pr-2 text-base text-g800 dark:text-g800 whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-p500 focus-visible:ring-offset-2",
				"cursor-pointer data-[placeholder]:text-g500 dark:data-[placeholder]:text-g500 transition-all select-none",
				"hover:bg-g50 dark:bg-g800/10 dark:hover:bg-g800/20",
				"[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:transition-transform",
				"[&_.icon]:fill-current",
				"[&_.loader]:h-4 [&_.loader]:w-4 [&_.loader]:animate-spin [&_.loader]:text-text",
				"disabled:cursor-not-allowed disabled:bg-g100 disabled:text-g300 disabled:hover:bg-g100 disabled:dark:bg-g800/10 disabled:dark:text-g400 disabled:dark:hover:bg-g800/10",
				"aria-invalid:border-error aria-invalid:data-[state=open]:border-error",
				"data-[size=medium]:h-10 data-[size=small]:h-8",
				"data-[state=open]:bg-g0 data-[state=open]:border-g50 dark:data-[state=open]:bg-g800/20 dark:data-[state=open]:border-g800/20 data-[state=open]:[&_.icon]:rotate-180",
				"*:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2",
				"data-[borderless=true]:bg-transparent dark:data-[borderless=true]:bg-transparent data-[borderless=true]:border-0 dark:data-[borderless=true]:border-0 data-[borderless=true]:p-0 dark:data-[borderless=true]:p-0",
				"data-[borderless=true]:hover:bg-transparent dark:data-[borderless=true]:hover:bg-transparent",
				"data-[borderless=true]:data-[state=open]:bg-transparent dark:data-[borderless=true]:data-[state=open]:bg-transparent data-[borderless=true]:data-[state=open]:border-0",
				className
			)}
			{...props}
		>
			{children}
			<SelectPrimitive.Icon asChild>{/* <ChevronDownIcon className="size-4 opacity-50" /> */}</SelectPrimitive.Icon>
		</SelectPrimitive.Trigger>
	)
}

function IconSelectTrigger({
	className,
	size = "small",
	color = "default",
	children,
	...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
	size?: "small" | "medium"
	color?: "default" | "ghost"
}) {
	return (
		<SelectPrimitive.Trigger
			data-slot="select-trigger"
			data-size={size}
			className={cn(
				"flex items-center justify-center rounded-lg border border-transparent whitespace-nowrap outline-none",
				"cursor-pointer transition-all select-none",
				"[&_svg]:pointer-events-none [&_svg]:shrink-0",
				"[&_.icon]:fill-current",
				"[&_.loader]:size-5 [&_.loader]:animate-spin [&_.loader]:text-text",
				"aria-invalid:border-error aria-invalid:data-[state=open]:border-error",
				"data-[size=medium]:size-10 data-[size=small]:size-8",
				"*:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2",
				color === "default" && [
					"bg-g100 text-g800 dark:text-g800",
					"hover:bg-g50 dark:bg-g800/10 dark:hover:bg-g800/20",
					"disabled:cursor-not-allowed disabled:bg-g100 disabled:text-g300 disabled:hover:bg-g100 disabled:dark:bg-g800/10 disabled:dark:text-g400 disabled:dark:hover:bg-g800/10",
					"data-[state=open]:bg-g0 data-[state=open]:border-g50 dark:data-[state=open]:bg-g800/20 dark:data-[state=open]:border-g800/20"
				],
				color === "ghost" && [
					"bg-transparent text-g700 dark:bg-transparent dark:text-g700",
					"hover:bg-g100 hover:text-g800 active:bg-g300 dark:hover:bg-g100 dark:hover:text-g800 dark:active:bg-g300",
					"disabled:cursor-not-allowed disabled:text-g400 dark:disabled:text-g400",
					"data-[state=open]:bg-g300 data-[state=open]:text-g800 dark:data-[state=open]:bg-g300 dark:data-[state=open]:text-g800"
				],
				className
			)}
			{...props}
		>
			{children}
		</SelectPrimitive.Trigger>
	)
}

function SelectContent({ className, children, position = "popper", ...props }: React.ComponentProps<typeof SelectPrimitive.Content>) {
	return (
		<SelectPrimitive.Portal>
			<SelectPrimitive.Content
				data-slot="select-content"
				className={cn(
					"bg-white/60 backdrop-blur-[20px]",
					"dark:bg-g250/60",
					"data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
					"data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
					"relative z-1500 max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto",
					"rounded-lg shadow-[0_2px_12px_rgba(0,0,0,0.15)]",
					position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
					className
				)}
				position={position}
				{...props}
			>
				<SelectScrollUpButton />
				<SelectPrimitive.Viewport
					className={cn("p-1", position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)] scroll-my-1")}
				>
					{children}
				</SelectPrimitive.Viewport>
				<SelectScrollDownButton />
			</SelectPrimitive.Content>
		</SelectPrimitive.Portal>
	)
}

function SelectLabel({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Label>) {
	return <SelectPrimitive.Label data-slot="select-label" className={cn("text-text-tertiary px-2 py-1.5 text-sm", className)} {...props} />
}

function SelectItem({ className, children, ...props }: React.ComponentProps<typeof SelectPrimitive.Item>) {
	return (
		<SelectPrimitive.Item
			data-slot="select-item"
			className={cn(
				"relative flex w-full cursor-pointer items-center gap-2 rounded-md p-2 text-base outline-hidden select-none",
				"data-[disabled]:pointer-events-none data-[disabled]:opacity-20",
				"[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg]:fill-current",
				"*:[span]:last:w-full *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
				"data-[state=checked]:bg-g800/30",
				"hover:bg-g800/30 focus:bg-g800/30",
				className
			)}
			{...props}
		>
			{/* <span className="absolute right-2 flex size-3.5 items-center justify-center">
				<SelectPrimitive.ItemIndicator>
					<TIcon name="check" size={16} />
				</SelectPrimitive.ItemIndicator>
			</span> */}
			<SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
		</SelectPrimitive.Item>
	)
}

function SelectSeparator({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.Separator>) {
	return <SelectPrimitive.Separator data-slot="select-separator" className={cn("bg-border pointer-events-none -mx-1 my-1 h-px", className)} {...props} />
}

function SelectScrollUpButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
	return (
		<SelectPrimitive.ScrollUpButton data-slot="select-scroll-up-button" className={cn("flex cursor-default items-center justify-center py-1", className)} {...props}>
			<ChevronUpIcon className="size-4" />
		</SelectPrimitive.ScrollUpButton>
	)
}

function SelectScrollDownButton({ className, ...props }: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
	return (
		<SelectPrimitive.ScrollDownButton data-slot="select-scroll-down-button" className={cn("flex cursor-default items-center justify-center py-1", className)} {...props}>
			<ChevronDownIcon className="size-4" />
		</SelectPrimitive.ScrollDownButton>
	)
}

export { IconSelectTrigger, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue }
